// Iter 81.1 / Phase E0.1 — HR Acceptance Dashboard.
// Top-of-page count cards + Recent + Pending lists with quick-jump
// to each document's acceptance trail.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, ShareNetwork, Eye, XCircle, ClockCounterClockwise, ArrowRight } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { formatDate } from "../../lib/format";

const STATUS_TONE = {
    Generated: "bg-slate-100 text-slate-700",
    Shared:    "bg-sky-100 text-sky-700",
    Viewed:    "bg-amber-100 text-amber-700",
    Accepted:  "bg-emerald-100 text-emerald-700",
    Declined:  "bg-rose-100 text-rose-700",
};

const CARDS = [
    { key: "Shared",   label: "Shared",   tone: "bg-sky-50 border-sky-200 text-sky-700",       icon: ShareNetwork },
    { key: "Viewed",   label: "Viewed",   tone: "bg-amber-50 border-amber-200 text-amber-700", icon: Eye },
    { key: "Accepted", label: "Accepted", tone: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: CheckCircle },
    { key: "Declined", label: "Declined", tone: "bg-rose-50 border-rose-200 text-rose-700",    icon: XCircle },
    { key: "Generated",label: "Pending Share", tone: "bg-slate-50 border-slate-200 text-slate-700", icon: ClockCounterClockwise },
];

export default function HrAcceptanceDashboard() {
    const navigate = useNavigate();
    const [data, setData] = useState({ counts: {}, recent: [], pending: [], pending_share: [] });
    const [loading, setLoading] = useState(true);
    // Iter 111.1 · obs #2 — click a KPI card to drill into that status.
    const [filterStatus, setFilterStatus] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data: payload } = await api.get("/hr/document/acceptance-dashboard");
            setData(payload);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load dashboard"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { Promise.resolve().then(() => { reload(); }); }, [reload]);

    // Build the drill-down list matching the selected KPI card.
    const drillRows = filterStatus === "Generated" ? (data.pending_share || [])
        : filterStatus ? (data.recent || []).filter((d) => d.share_status === filterStatus)
        : [];

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1400px]" data-testid="hr-acceptance-dashboard">
            <PageHeader
                eyebrow="HR Operations"
                title="Document Acceptance Dashboard"
                description="Track the lifecycle of every generated HR document — from share, to view, to acceptance / decline."
            />

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
                {CARDS.map((c) => {
                    const Icon = c.icon;
                    const isSelected = filterStatus === c.key;
                    const clickable = (data.counts[c.key] || 0) > 0;
                    return (
                        <button
                            type="button"
                            key={c.key}
                            data-testid={`acceptance-card-${c.key.toLowerCase()}`}
                            onClick={() => clickable && setFilterStatus(isSelected ? null : c.key)}
                            title={clickable ? `Click to see ${c.label} documents` : `No ${c.label} documents`}
                            disabled={!clickable}
                            className={`rounded-xl border p-4 text-left transition ${c.tone} ${clickable ? "cursor-pointer hover:shadow-md" : "cursor-default opacity-70"} ${isSelected ? "ring-2 ring-indigo-500" : ""}`}
                        >
                            <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider font-semibold">
                                <Icon size={14} /> {c.label}
                            </div>
                            <div className="text-3xl font-bold">{loading ? "…" : (data.counts[c.key] || 0)}</div>
                            {clickable && <div className="text-[10px] mt-1 opacity-70">{isSelected ? "showing below ↓" : "click to view"}</div>}
                        </button>
                    );
                })}
            </div>

            {/* Iter 111.1 · Drill-down list for the selected KPI ── */}
            {filterStatus && (
                <section className="mt-6 bg-white rounded-xl border border-indigo-200 p-5" data-testid="acceptance-drill-down-section">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-slate-800">
                            {CARDS.find((c) => c.key === filterStatus)?.label} · {drillRows.length} document{drillRows.length === 1 ? "" : "s"}
                        </h3>
                        <Button size="sm" variant="ghost" onClick={() => setFilterStatus(null)}>Clear filter</Button>
                    </div>
                    {drillRows.length === 0 ? (
                        <div className="text-sm text-slate-400 py-4">
                            {filterStatus === "Generated"
                                ? "No documents are currently in the Generated / Pending Share state."
                                : `The 30-document recent window does not include any ${filterStatus} rows. Older docs remain available on the individual employee profile.`}
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 text-xs">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Document</th>
                                    <th className="text-left px-3 py-2 font-medium">Employee</th>
                                    <th className="text-left px-3 py-2 font-medium">Category</th>
                                    <th className="text-left px-3 py-2 font-medium">Generated / Shared</th>
                                    <th className="w-10" />
                                </tr>
                            </thead>
                            <tbody>
                                {drillRows.map((d) => (
                                    <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/employees/${d.employee_id}`)} data-testid={`drill-row-${d.id}`}>
                                        <td className="px-3 py-2 text-slate-800">{d.rendered_title}<div className="text-[10px] text-slate-400">v{d.template_version}</div></td>
                                        <td className="px-3 py-2 text-slate-700">{d.employee_name} <span className="text-xs text-slate-400">({d.employee_code})</span></td>
                                        <td className="px-3 py-2"><Badge variant="outline">{d.category}</Badge></td>
                                        <td className="px-3 py-2 text-xs text-slate-500">{formatDate(d.shared_at || d.generated_at || d.created_at)}</td>
                                        <td className="px-3 py-2 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>
            )}

            <section className="mt-6 bg-white rounded-xl border border-slate-200 p-5" data-testid="acceptance-pending-section">
                <h3 className="text-sm font-semibold mb-3 text-slate-800">Pending Acceptance ({data.pending.length})</h3>
                {loading && <div className="text-sm text-slate-400">Loading…</div>}
                {!loading && data.pending.length === 0 && (
                    <div className="text-sm text-slate-400 py-6 text-center">No pending acceptances.</div>
                )}
                {!loading && data.pending.length > 0 && (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Document</th>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Category</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Shared</th>
                                <th className="text-left px-3 py-2 font-medium">Views</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.pending.map((d) => (
                                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/employees/${d.employee_id}`)}>
                                    <td className="px-3 py-2 text-slate-800">{d.rendered_title}<div className="text-[10px] text-slate-400">v{d.template_version}</div></td>
                                    <td className="px-3 py-2 text-slate-700">{d.employee_name} <span className="text-xs text-slate-400">({d.employee_code})</span></td>
                                    <td className="px-3 py-2"><Badge variant="outline">{d.category}</Badge></td>
                                    <td className="px-3 py-2"><Badge className={STATUS_TONE[d.share_status]}>{d.share_status}</Badge></td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{formatDate(d.shared_at)}</td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{d.view_count || 0}</td>
                                    <td className="px-3 py-2 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <section className="mt-6 bg-white rounded-xl border border-slate-200 p-5" data-testid="acceptance-recent-section">
                <h3 className="text-sm font-semibold mb-3 text-slate-800">Recent Activity (last 30)</h3>
                {loading && <div className="text-sm text-slate-400">Loading…</div>}
                {!loading && data.recent.length === 0 && (
                    <div className="text-sm text-slate-400 py-6 text-center">No recent acceptance activity.</div>
                )}
                {!loading && data.recent.length > 0 && (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Document</th>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Category</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Latest</th>
                                <th className="w-10" />
                            </tr>
                        </thead>
                        <tbody>
                            {data.recent.map((d) => (
                                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/employees/${d.employee_id}`)}>
                                    <td className="px-3 py-2 text-slate-800">{d.rendered_title}<div className="text-[10px] text-slate-400">v{d.template_version}</div></td>
                                    <td className="px-3 py-2 text-slate-700">{d.employee_name} <span className="text-xs text-slate-400">({d.employee_code})</span></td>
                                    <td className="px-3 py-2"><Badge variant="outline">{d.category}</Badge></td>
                                    <td className="px-3 py-2"><Badge className={STATUS_TONE[d.share_status]}>{d.share_status}</Badge></td>
                                    <td className="px-3 py-2 text-xs text-slate-500">
                                        {d.accepted_at ? `Accepted ${formatDate(d.accepted_at)}` : d.declined_at ? `Declined ${formatDate(d.declined_at)}` : `Shared ${formatDate(d.shared_at)}`}
                                    </td>
                                    <td className="px-3 py-2 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}
